import { useMutation, useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useBlocker, useNavigate } from "react-router";
import { queryClient, trpc } from "../../app/trpc.js";
import { Button, buttonClass, CheckIcon, focusRing, Notice, PageTitle, Sheet, Spinner, useWide } from "../../ui/index.js";
import { BackLink, useCabinet } from "../cabinet/index.js";
import { AssistantStep } from "./steps/AssistantStep.js";
import { BookingsStep } from "./steps/BookingsStep.js";
import { DoneStep } from "./steps/DoneStep.js";
import { InstagramStep } from "./steps/InstagramStep.js";
import { MastersStep } from "./steps/MastersStep.js";
import { RulesStep } from "./steps/RulesStep.js";
import { ServicesStep } from "./steps/ServicesStep.js";
import { TemplateStep } from "./steps/TemplateStep.js";
import { TestStep } from "./steps/TestStep.js";
import { type OnboardingState, STEPS, type StepId, type StepProps } from "./types.js";
import {
  confirmLeave,
  type GuardState,
  initialGuard,
  reportDirty,
  requestLeave,
  shouldBlockNavigation,
  stay,
  UnsavedContext,
  type UnsavedContextValue,
  useBeforeUnloadGuard,
} from "./unsaved.js";

const STEP_VIEWS: Record<StepId, (p: StepProps) => React.JSX.Element> = {
  template: TemplateStep,
  services: ServicesStep,
  masters: MastersStep,
  rules: RulesStep,
  assistant: AssistantStep,
  bookings: BookingsStep,
  test: TestStep,
  instagram: InstagramStep,
  done: DoneStep,
};

/**
 * Мастер настройки салона (~15 минут): шаблон ниши → услуги → мастера и графики → правила → перенос записей →
 * кто отвечает → перенос записей → тестовый чат → Instagram → итог со ссылкой и QR.
 * Шаг хранится на сервере: можно прерваться и вернуться.
 * Навигация на телефоне: внизу «Назад» на предыдущий шаг (на первом шаге: в «Ещё»),
 * в шапке мастера ссылка «В меню» на «Ещё» с любого шага. Шапка кабинета своей «Назад» здесь не показывает
 * (`headerBackTo`), чтобы кнопки не двоились.
 * Шаг с несохранёнными правками сообщает об этом через `useUnsavedChanges`; тогда одна и та же шторка
 * («Остаться» / «Уйти») спрашивает подтверждение в двух случаях: переход по шагам внутри мастера (список шагов,
 * полоска прогресса, нижние «Назад» и «Далее» без сохранения; адрес не меняется, работает отложенный переход `leave`)
 * и уход на другой маршрут (боковая панель, нижние вкладки, «В меню», нижняя «Назад» с первого шага, «назад» и «вперёд»
 * браузера; работает блокировщик роутера). Каждый уход спрашивает один раз: переходы на другой маршрут идут только
 * через блокировщик, не через `leave`. Закрытие и перезагрузка вкладки предупреждает браузер (`beforeunload`).
 * Открывают владелец и админ (пакет «подключим за вас»). После завершения пункта в «Ещё» владельца нет (решение 04.11.2026):
 * пройти мастер снова можно из кабинета салона в админке или по прямой ссылке; прайс загружают на странице «Услуги».
 */
export function OnboardingPage() {
  const { t } = useTranslation();
  const wide = useWide();
  const navigate = useNavigate();
  const cab = useCabinet();
  const salonId = cab.salonId;
  const key = trpc.onboarding.state.queryKey({ salonId });
  const state = useQuery(trpc.onboarding.state.queryOptions({ salonId }, { enabled: Boolean(salonId) }));
  const setState = (s: OnboardingState) => queryClient.setQueryData(key, s);
  const setStep = useMutation(trpc.onboarding.setStep.mutationOptions({ onSuccess: setState }));
  const [guard, setGuard] = useState<GuardState>(initialGuard);
  const unsaved = useMemo<UnsavedContextValue>(() => ({ report: (id, dirty) => setGuard((g) => reportDirty(g, id, dirty)) }), []);
  const dirty = guard.dirty.size > 0;
  /** Переход на другой шаг мастера: без правок сразу, с правками после ответа в шторке. */
  const leave = (go: () => void) => {
    const r = requestLeave(guard, go);
    if (!r.left) setGuard(r.state);
  };
  // Уход на другой маршрут: роутер останавливает переход, шторка ниже предлагает остаться или уйти.
  const blocker = useBlocker(({ currentLocation, nextLocation }) => shouldBlockNavigation(dirty, currentLocation.pathname, nextLocation.pathname));
  useBeforeUnloadGuard(dirty);
  const asking = guard.pending !== null || blocker.state === "blocked";
  const stayHere = () => {
    setGuard(stay);
    if (blocker.state === "blocked") blocker.reset();
  };
  const leaveAnyway = () => {
    if (blocker.state === "blocked") blocker.proceed();
    else setGuard(confirmLeave(guard));
  };

  if (state.isPending) return <Spinner />;
  if (state.isError || !state.data) return <Notice kind="error">{t("common.error")}</Notice>;
  const s = state.data;
  const index = STEPS.indexOf(s.step);
  const goTo = (step: StepId, done?: StepId) => {
    setStep.mutate({ salonId, step, ...(done ? { done } : {}) });
    window.scrollTo({ top: 0 });
  };
  const View = STEP_VIEWS[s.step];
  const canOpen = (step: StepId, i: number) => s.done.includes(step) || i <= index;
  // На телефоне полоска прогресса; на широком экране список шагов слева, пройденные и текущий кликабельны.
  const steps = wide ? (
    <nav aria-label={t("onb.progress")} className="flex flex-col gap-1">
      {STEPS.map((step, i) => (
        <button
          key={step}
          type="button"
          aria-current={i === index ? "step" : undefined}
          disabled={!canOpen(step, i)}
          onClick={() => (i === index ? undefined : leave(() => goTo(step)))}
          className={`flex items-center gap-3 rounded-field px-3 py-2 text-left text-sm ${focusRing} ${
            i === index ? "bg-brand-soft font-semibold text-brand-text" : s.done.includes(step) ? "text-ink hover:bg-slate-100" : "text-ink-3"
          }`}
        >
          <span
            className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
              i === index ? "bg-brand text-white" : s.done.includes(step) ? "bg-emerald-500 text-white" : "border border-line-strong bg-white text-ink-3"
            }`}
            aria-hidden="true"
          >
            {s.done.includes(step) && i !== index ? <CheckIcon className="h-3.5 w-3.5" /> : i + 1}
          </span>
          <span className="truncate">{t(`onb.step.${step}`)}</span>
        </button>
      ))}
    </nav>
  ) : (
    <>
      <nav aria-label={t("onb.progress")} className="mb-4 flex gap-1">
        {STEPS.map((step, i) => (
          <button
            key={step}
            type="button"
            title={t(`onb.step.${step}`)}
            aria-current={i === index ? "step" : undefined}
            onClick={() => (canOpen(step, i) && i !== index ? leave(() => goTo(step)) : undefined)}
            className={`h-1.5 flex-1 rounded-full ${i === index ? "bg-brand" : s.done.includes(step) ? "bg-emerald-500" : "bg-slate-200"}`}
          />
        ))}
      </nav>
      <p className="mb-4 text-sm text-ink-3">{t("onb.stepOf", { n: index + 1, total: STEPS.length, name: t(`onb.step.${s.step}`) })}</p>
    </>
  );
  // С первого шага «Назад» ведёт в «Ещё»: это другой маршрут, при правках спросит блокировщик, а не `leave`.
  const goBack = (opts?: { saved?: boolean }) => {
    const prev = STEPS[index - 1];
    if (!prev) navigate(`${cab.base}/settings`);
    else if (opts?.saved) goTo(prev);
    else leave(() => goTo(prev));
  };
  const goNext = () => {
    const nextStep = STEPS[index + 1];
    if (nextStep) goTo(nextStep, s.step);
  };
  return (
    <div>
      {/* Обычная ссылка: при правках переход остановит блокировщик роутера; открытие в новой вкладке правок не теряет. */}
      <BackLink label={t("onb.toMenu")} className={buttonClass("secondary", "mb-4")} />
      <PageTitle>{t("onb.title")}</PageTitle>
      <div className={wide ? "grid grid-cols-[14rem_minmax(0,1fr)] gap-8" : ""}>
        <div>{steps}</div>
        <div className="min-w-0">
          {wide && (
            <h2 className="mb-4 text-xl font-semibold text-ink">{t("onb.stepOf", { n: index + 1, total: STEPS.length, name: t(`onb.step.${s.step}`) })}</h2>
          )}
          {setStep.isError && <Notice kind="error">{t("common.error")}</Notice>}
          <UnsavedContext.Provider value={unsaved}>
            <View salonId={salonId} state={s} setState={setState} next={(opts) => (opts?.saved ? goNext() : leave(goNext))} back={goBack} />
          </UnsavedContext.Provider>
        </div>
      </div>
      <Sheet open={asking} onClose={stayHere} title={t("onb.unsaved.title")}>
        <p className="text-ink-2">{t("onb.unsaved.text")}</p>
        <div className="mt-4 flex gap-2">
          <Button variant="secondary" className="flex-1" onClick={leaveAnyway}>
            {t("onb.unsaved.leave")}
          </Button>
          <Button className="flex-1" onClick={stayHere}>
            {t("onb.unsaved.stay")}
          </Button>
        </div>
      </Sheet>
    </div>
  );
}
